import React, { useState, useRef, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, IconButton, Avatar, Fab,
  Drawer, Badge, Chip, Divider, Tabs, Tab, Button, CircularProgress,
  List, ListItem, ListItemText, Slider, Alert, LinearProgress, Tooltip
} from '@mui/material';
import {
  Send as SendIcon,
  SmartToy as SmartToyIcon,
  Close as CloseIcon,
  AutoAwesome as AutoAwesomeIcon,
  CalendarMonth as CalendarIcon,
  Flag as FlagIcon,
  BatteryChargingFull as EnergyIcon,
  Lightbulb as IdeaIcon,
  AccessTime as TimeIcon,
  CheckCircle as CheckCircleIcon,
  Assignment as TaskIcon,
  Folder as ProjectIcon,
  Group as GroupIcon,
  Warning as WarningIcon,
  BarChart as StatsIcon,
  Chat as ChatIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { aiService } from '../services/api';

// ─── Helpers ────────────────────────────────────────────────────────────────

const STATUS_COLORS = {
  not_started: '#9e9e9e', in_progress: '#2196f3',
  completed: '#4caf50', blocked: '#f44336'
};
const PRIORITY_COLORS = {
  low: '#8bc34a', medium: '#ff9800', high: '#f44336', urgent: '#9c27b0'
};

const useStatusLabels = () => {
  const { t } = useTranslation();
  return {
    not_started: t('tasks.statusNotStarted'),
    in_progress: t('tasks.statusInProgress'),
    completed: t('tasks.statusCompleted'),
    blocked: t('tasks.statusBlocked')
  };
};

const usePriorityLabels = () => {
  const { t } = useTranslation();
  return {
    low: t('tasks.priorityLow'),
    medium: t('tasks.priorityMedium'),
    high: t('tasks.priorityHigh'),
    urgent: t('tasks.priorityUrgent')
  };
};

// ─── Action Result Renderer ─────────────────────────────────────────────────

function ProjectListResult({ projects }) {
  const { t } = useTranslation();
  if (!projects || projects.length === 0) {
    return <Typography variant="body2" color="text.secondary">{t('assistant.noProjects')}</Typography>;
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
      {projects.map(p => {
        const pct = p.total_tasks > 0 ? Math.round((p.completed_tasks / p.total_tasks) * 100) : 0;
        return (
          <Paper key={p.id} elevation={1} sx={{ p: 1.5, borderLeft: `4px solid ${p.color || '#3498db'}`, borderRadius: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ flex: 1 }}>{p.title}</Typography>
              <Chip
                label={p.status === 'active' ? t('assistant.statusActive') : t('assistant.statusArchived')}
                size="small"
                sx={{ height: 18, fontSize: '0.65rem', bgcolor: p.status === 'active' ? '#e8f5e9' : '#f5f5f5', color: p.status === 'active' ? '#2e7d32' : '#757575' }}
              />
            </Box>
            {p.domain && <Typography variant="caption" color="text.secondary">{p.domain}</Typography>}
            <Box sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                <Typography variant="caption" color="text.secondary">
                  {p.completed_tasks || 0}/{p.total_tasks || 0} tâches
                </Typography>
                <Typography variant="caption" fontWeight="bold" color="primary">{pct}%</Typography>
              </Box>
              <LinearProgress variant="determinate" value={pct} sx={{ height: 5, borderRadius: 2 }} />
            </Box>
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                👥 {p.team_members || 1} {t('assistant.memberCount', { count: p.team_members || 1 })}
              </Typography>
              {p.end_date && (
                <Typography variant="caption" color="text.secondary">
                  📅 {new Date(p.end_date).toLocaleDateString('fr-FR')}
                </Typography>
              )}
            </Box>
          </Paper>
        );
      })}
    </Box>
  );
}

function TaskListResult({ tasks, title }) {
  const { t } = useTranslation();
  const STATUS_LABELS = useStatusLabels();
  const PRIORITY_LABELS = usePriorityLabels();
  if (!tasks || tasks.length === 0) {
    return <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{t('assistant.noTasks')}</Typography>;
  }
  return (
    <Box sx={{ mt: 1 }}>
      {title && <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>{title}</Typography>}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
        {tasks.slice(0, 10).map(t => (
          <Paper key={t.id} elevation={0} sx={{ p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ flex: 1, fontWeight: 500, fontSize: '0.8rem' }}>{t.title}</Typography>
              <Chip
                label={STATUS_LABELS[t.status] || t.status}
                size="small"
                sx={{ height: 18, fontSize: '0.6rem', bgcolor: STATUS_COLORS[t.status] + '22', color: STATUS_COLORS[t.status], border: `1px solid ${STATUS_COLORS[t.status]}33` }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1, mt: 0.3 }}>
              <Chip
                label={PRIORITY_LABELS[t.priority] || t.priority}
                size="small"
                sx={{ height: 16, fontSize: '0.6rem', bgcolor: PRIORITY_COLORS[t.priority] + '22', color: PRIORITY_COLORS[t.priority] }}
              />
              {t.assignee_name && (
                <Typography variant="caption" color="text.secondary">👤 {t.assignee_name}</Typography>
              )}
              {t.end_date && t.end_date !== '0000-00-00' && (
                <Typography variant="caption" color={new Date(t.end_date) < new Date() ? 'error' : 'text.secondary'}>
                  📅 {new Date(t.end_date).toLocaleDateString('fr-FR')}
                </Typography>
              )}
            </Box>
          </Paper>
        ))}
        {tasks.length > 10 && (
          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
            {t('assistant.andMore', { count: tasks.length - 10 })}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function ProjectStatsResult({ project, overdue_count, blocked_count }) {
  const { t } = useTranslation();
  if (!project) return null;
  const total = project.total_tasks || 0;
  const completed = project.completed_tasks || 0;
  const inProgress = project.in_progress_tasks || 0;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

  const stats = [
    { label: t('assistant.statTotal'), value: total, color: '#6366f1', icon: '📋' },
    { label: t('assistant.statCompleted'), value: completed, color: '#4caf50', icon: '✅' },
    { label: t('assistant.statInProgress'), value: inProgress, color: '#2196f3', icon: '🔄' },
    { label: t('assistant.statBlocked'), value: blocked_count || 0, color: '#f44336', icon: '🚫' },
    { label: t('assistant.statOverdue'), value: overdue_count || 0, color: '#ff9800', icon: '⚠️' },
    { label: t('assistant.statMembers'), value: project.team_members || 0, color: '#9c27b0', icon: '👥' },
  ];

  return (
    <Box sx={{ mt: 1 }}>
      <Box sx={{ mb: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" fontWeight="bold">{t('assistant.globalProgress')}</Typography>
          <Typography variant="caption" fontWeight="bold" color="primary">{pct}%</Typography>
        </Box>
        <LinearProgress variant="determinate" value={pct} sx={{ height: 8, borderRadius: 4 }} />
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.8 }}>
        {stats.map(s => (
          <Paper key={s.label} elevation={0} sx={{ p: 1, textAlign: 'center', border: `1px solid ${s.color}33`, borderRadius: 1 }}>
            <Typography variant="caption" sx={{ display: 'block', mb: 0.2 }}>{s.icon}</Typography>
            <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: s.color, lineHeight: 1 }}>{s.value}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>{s.label}</Typography>
          </Paper>
        ))}
      </Box>
    </Box>
  );
}

function DiscussionSummaryResult({ summary, message_count }) {
  const { t } = useTranslation();
  if (!summary) return null;
  return (
    <Box sx={{ mt: 1 }}>
      {message_count && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
          {t('assistant.basedOn', { count: message_count })}
        </Typography>
      )}
      <Paper elevation={0} sx={{ p: 1.5, bgcolor: '#f8f9ff', border: '1px solid #e8eaf6', borderRadius: 1 }}>
        <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 1 }}>{summary.summary}</Typography>
        {summary.key_points && summary.key_points.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" fontWeight="bold" color="primary">{t('assistant.keyPoints')}</Typography>
            {summary.key_points.map((p, i) => (
              <Typography key={i} variant="caption" sx={{ display: 'flex', gap: 0.5, mt: 0.3 }}>
                <span>•</span> {p}
              </Typography>
            ))}
          </Box>
        )}
        {summary.decisions && summary.decisions.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" fontWeight="bold" color="success.main">{t('assistant.decisions')}</Typography>
            {summary.decisions.map((d, i) => (
              <Typography key={i} variant="caption" sx={{ display: 'flex', gap: 0.5, mt: 0.3 }}>
                <span>✔</span> {d}
              </Typography>
            ))}
          </Box>
        )}
        {summary.action_items && summary.action_items.length > 0 && (
          <Box>
            <Typography variant="caption" fontWeight="bold" color="warning.main">{t('assistant.actionItems')}</Typography>
            {summary.action_items.map((a, i) => (
              <Typography key={i} variant="caption" sx={{ display: 'flex', gap: 0.5, mt: 0.3 }}>
                <span>→</span> {a}
              </Typography>
            ))}
          </Box>
        )}
      </Paper>
    </Box>
  );
}

function TeamMembersResult({ members }) {
  const { t } = useTranslation();
  if (!members || members.length === 0) return null;
  const roleLabels = {
    owner: t('assistant.roleOwner'),
    admin: t('assistant.roleAdmin'),
    editor: t('assistant.roleEditor'),
    viewer: t('assistant.roleViewer')
  };
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8, mt: 1 }}>
      {members.map((m, i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 0.8, bgcolor: '#f8f9fa', borderRadius: 1 }}>
          <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: m.avatar_url || m.avatar_color || '#6366f1' }}>
            {(m.name || m.full_name || '?')[0]?.toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem', lineHeight: 1.2 }}>
              {m.name || m.full_name}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
              {m.functional_role || roleLabels[m.role] || m.role}
            </Typography>
          </Box>
          <Chip label={roleLabels[m.role] || m.role} size="small"
            sx={{ height: 16, fontSize: '0.6rem', bgcolor: m.role === 'owner' ? '#fce4ec' : '#f3e5f5' }} />
        </Box>
      ))}
    </Box>
  );
}

function TaskActionSuccessResult({ task }) {
  const STATUS_LABELS = useStatusLabels();
  if (!task) return null;
  return (
    <Paper elevation={0} sx={{ p: 1.5, bgcolor: '#e8f5e9', border: '1px solid #c8e6c9', borderRadius: 1, mt: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 18 }} />
        <Box>
          <Typography variant="body2" fontWeight="bold">{task.title}</Typography>
          {task.status && (
            <Chip label={STATUS_LABELS[task.status] || task.status} size="small"
              sx={{ height: 16, fontSize: '0.6rem', bgcolor: STATUS_COLORS[task.status] + '22', color: STATUS_COLORS[task.status], mt: 0.3 }} />
          )}
          {task.assignees && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.2 }}>
              👤 {task.assignees.join(', ')}
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );
}

function ActionResultRenderer({ action, result }) {
  const { t } = useTranslation();
  if (!result || action === 'NONE') return null;

  switch (action) {
    case 'LIST_PROJECTS':
      return (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <ProjectIcon sx={{ fontSize: 14, color: '#6366f1' }} />
            <Typography variant="caption" fontWeight="bold" color="primary">
              {t('assistant.projectCount', { count: result.projects?.length || 0 })}
            </Typography>
          </Box>
          <ProjectListResult projects={result.projects} />
        </Box>
      );

    case 'LIST_TASKS':
      return (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <TaskIcon sx={{ fontSize: 14, color: '#2196f3' }} />
            <Typography variant="caption" fontWeight="bold" color="primary">
              {t('assistant.taskCount', { count: result.tasks?.length || 0 })}
            </Typography>
          </Box>
          <TaskListResult tasks={result.tasks} />
        </Box>
      );

    case 'GET_OVERDUE_TASKS':
      return (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <WarningIcon sx={{ fontSize: 14, color: '#ff9800' }} />
            <Typography variant="caption" fontWeight="bold" color="warning.main">
              {t('assistant.overdueCount', { count: result.tasks?.length || 0 })}
            </Typography>
          </Box>
          <TaskListResult tasks={result.tasks} />
        </Box>
      );

    case 'GET_PROJECT_STATS':
      return (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <StatsIcon sx={{ fontSize: 14, color: '#6366f1' }} />
            <Typography variant="caption" fontWeight="bold" color="primary">{t('assistant.stats')}</Typography>
          </Box>
          <ProjectStatsResult {...result} />
        </Box>
      );

    case 'SUMMARIZE_DISCUSSIONS':
      return (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <ChatIcon sx={{ fontSize: 14, color: '#6366f1' }} />
            <Typography variant="caption" fontWeight="bold" color="primary">{t('assistant.summary')}</Typography>
          </Box>
          <DiscussionSummaryResult summary={result.summary} message_count={result.message_count} />
        </Box>
      );

    case 'LIST_TEAM_MEMBERS':
      return (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <GroupIcon sx={{ fontSize: 14, color: '#6366f1' }} />
            <Typography variant="caption" fontWeight="bold" color="primary">
              {t('assistant.memberCount', { count: result.members?.length || 0 })}
            </Typography>
          </Box>
          <TeamMembersResult members={result.members} />
        </Box>
      );

    case 'CREATE_TASK':
      return <TaskActionSuccessResult task={result.task} />;

    case 'UPDATE_TASK_STATUS':
    case 'ASSIGN_TASK':
      return <TaskActionSuccessResult task={result.task} />;

    default:
      return null;
  }
}

// ─── Tab Panels ─────────────────────────────────────────────────────────────

function PlanningTab({ projectId }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [habits, setHabits] = useState('09:00 - 17:00');

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const response = await aiService.smartSchedule({ projectId, habits });
      setResult(response.data.suggestions);
    } catch (error) {
      console.error('Error generating schedule:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="subtitle2" gutterBottom fontWeight="bold">{t('assistant.planning')}</Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        {t('assistant.planningDesc')}
      </Typography>
      <TextField label={t('assistant.planningHabits')} fullWidth size="small" value={habits}
        onChange={(e) => setHabits(e.target.value)} placeholder="Ex: 9h-18h, pause à 13h" sx={{ mb: 2 }} />
      <Button variant="contained" fullWidth
        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AutoAwesomeIcon />}
        onClick={handleGenerate} disabled={loading}
        sx={{ background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)', color: 'white' }}>
        {loading ? t('assistant.planningGenerating') : t('assistant.planningGenerate')}
      </Button>
      {result && (
        <Box sx={{ flex: 1, overflowY: 'auto', mt: 2 }}>
          {result.general_advice && (
            <Alert severity="info" sx={{ mb: 2 }} icon={<IdeaIcon />}>{result.general_advice}</Alert>
          )}
          {(result.suggestions || []).map((s, i) => (
            <Paper key={i} elevation={1} sx={{ mb: 2, p: 2, borderLeft: '4px solid #2196F3' }}>
              <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom>
                <TimeIcon sx={{ fontSize: 16, verticalAlign: 'text-bottom', mr: 0.5 }} />
                {s.suggested_time}
              </Typography>
              {s.task_title && <Typography variant="subtitle2" color="text.primary" fontWeight="bold" gutterBottom>{s.task_title}</Typography>}
              <Typography variant="body1" gutterBottom>{s.message}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                💡 {s.reasoning}
              </Typography>
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  );
}

function PrioritiesTab() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [energy, setEnergy] = useState('medium');

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const energyMap = { low: 'Faible', medium: 'Moyen', high: 'Élevé' };
      const response = await aiService.getDailyPriorities({ energyLevel: energyMap[energy] || 'Moyen' });
      setResult(response.data.result);
    } catch (error) {
      console.error('Error generating priorities:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="subtitle2" gutterBottom fontWeight="bold">{t('assistant.priorities')}</Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        {t('assistant.prioritiesDesc')}
      </Typography>
      <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
        <Typography gutterBottom variant="caption">
          {t('assistant.energyLevel', { level: t(`assistant.energy_${energy}`) })}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <EnergyIcon color={energy === 'low' ? 'action' : 'primary'} />
          <Slider step={null}
            marks={[{ value: 0, label: t('assistant.energy_low') }, { value: 50, label: t('assistant.energy_medium') }, { value: 100, label: t('assistant.energy_high') }]}
            value={energy === 'low' ? 0 : energy === 'medium' ? 50 : 100}
            onChange={(e, val) => setEnergy(val === 0 ? 'low' : val === 50 ? 'medium' : 'high')}
            sx={{ flex: 1 }} />
        </Box>
        <Button variant="contained" fullWidth
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <FlagIcon />}
          onClick={handleGenerate} disabled={loading}
          sx={{ mt: 2, bgcolor: '#ff9800', '&:hover': { bgcolor: '#f57c00' } }}>
          {loading ? t('assistant.analyzing') : t('assistant.myTasks')}
        </Button>
      </Box>
      {result && (
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {result.summary && (
            <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic', textAlign: 'center' }}>
              "{result.summary}"
            </Typography>
          )}
          {(result.top_tasks || []).map((task, index) => (
            <Paper key={index} elevation={2} sx={{ mb: 2, p: 0, overflow: 'hidden', border: index === 0 ? '2px solid #ff9800' : '1px solid #e0e0e0' }}>
              <Box sx={{ bgcolor: index === 0 ? '#ff9800' : '#f5f5f5', color: index === 0 ? 'white' : 'text.primary', p: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2" fontWeight="bold">#{index + 1}</Typography>
                {index === 0 && <Chip label={t('assistant.topPriority')} size="small" sx={{ bgcolor: 'white', color: '#ff9800', fontWeight: 'bold', height: 20 }} />}
              </Box>
              <Box sx={{ p: 2 }}>
                <Typography variant="body1" fontWeight="medium" gutterBottom>{task.task_title || `Tâche #${task.task_id}`}</Typography>
                <Typography variant="body2" color="text.secondary" paragraph>{task.reason}</Typography>
                <Chip icon={<EnergyIcon sx={{ fontSize: '1rem !important' }} />} label={task.energy_match}
                  size="small" variant="outlined" color="primary" sx={{ fontSize: '0.75rem' }} />
              </Box>
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  );
}

// ─── Exemples de commandes ─── defined inside component to use t() ────────────

// ─── ERA Assistant Chat ──────────────────────────────────────────────────────

function ERAAssistant({ projectId = null, open, onClose }) {
  const { t } = useTranslation();
  const [tabValue, setTabValue] = useState(0);

  const QUICK_ACTIONS = [
    { label: t('assistant.qa_listProjects'), icon: '📁' },
    { label: t('assistant.qa_overdueTasks'), icon: '⚠️' },
    { label: t('assistant.qa_stats'), icon: '📊' },
    { label: t('assistant.qa_summary'), icon: '💬' },
    { label: t('assistant.qa_team'), icon: '👥' },
    { label: t('assistant.qa_inProgress'), icon: '🔄' },
  ];

  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: t('assistant.welcomeMessage')
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  useEffect(() => { scrollToBottom(); }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const token = localStorage.getItem('era_token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/ai/chat`,
        {
          messages: [...messages, userMessage].map(m => ({ role: m.role, content: m.content })),
          projectId
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.data.response,
        action: response.data.action,
        action_result: response.data.action_result
      }]);
    } catch (error) {
      console.error('Chat error:', error);
      const serverMsg = error.response?.data?.error || '';
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: serverMsg ? `❌ ${serverMsg}` : `❌ ${t('assistant.error')}`
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const renderMessageContent = (content) => ({
    __html: (content || '')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\n/g, '<br />')
  });

  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 380 },
          height: { xs: '100%', sm: '80vh' },
          maxHeight: 700,
          top: { xs: 0, sm: 'auto' },
          bottom: { xs: 0, sm: 20 },
          boxSizing: 'border-box',
          borderTopLeftRadius: 16,
          borderBottomLeftRadius: 16,
          borderTopRightRadius: { xs: 0, sm: 16 },
          borderBottomRightRadius: { xs: 0, sm: 16 },
          overflow: 'hidden',
          boxShadow: '0 10px 40px rgba(0,0,0,0.15)'
        }
      }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#f8f9fa' }}>

        {/* Header */}
        <Paper elevation={0} sx={{
          p: 2, display: 'flex', alignItems: 'center', gap: 2, borderRadius: 0,
          background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
          color: 'white', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
        }}>
          <Badge overlap="circular" anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} variant="dot"
            sx={{ '& .MuiBadge-badge': { bgcolor: '#44b700', color: '#44b700', boxShadow: '0 0 0 2px white' } }}>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 42, height: 42, border: '2px solid rgba(255,255,255,0.5)' }}>
              <SmartToyIcon sx={{ color: 'white' }} />
            </Avatar>
          </Badge>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>ERA Assistant</Typography>
            <Typography variant="caption" sx={{ opacity: 0.9, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#44b700', display: 'inline-block' }}></span>
              {projectId ? t('assistant.projectContext') : t('assistant.online')}
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
            <CloseIcon />
          </IconButton>
        </Paper>

        {/* Tabs */}
        <Box sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} variant="fullWidth"
            sx={{ minHeight: 40, '& .MuiTab-root': { minHeight: 40, fontSize: '0.75rem', py: 0.5 } }}>
            <Tab icon={<SmartToyIcon sx={{ fontSize: 16 }} />} iconPosition="start" label={t('assistant.tabChat')} />
            <Tab icon={<CalendarIcon sx={{ fontSize: 16 }} />} iconPosition="start" label={t('assistant.tabPlanning')} />
            <Tab icon={<FlagIcon sx={{ fontSize: 16 }} />} iconPosition="start" label={t('assistant.tabPriorities')} />
          </Tabs>
        </Box>

        {/* Tab Chat */}
        {tabValue === 0 && (
          <>
            {/* Quick actions */}
            {messages.length === 1 && (
              <Box sx={{ p: 1.5, bgcolor: 'background.paper' }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.8, display: 'block', fontWeight: 500 }}>
                  {t('assistant.quickActions')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                  {QUICK_ACTIONS.map((a, i) => (
                    <Chip key={i} label={`${a.icon} ${a.label}`} size="small"
                      onClick={() => setInput(a.label)}
                      sx={{ cursor: 'pointer', fontSize: '0.7rem', '&:hover': { bgcolor: '#ede9fe' } }} />
                  ))}
                </Box>
              </Box>
            )}

            <Divider />

            {/* Messages */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {messages.map((msg, idx) => (
                <Box key={idx} sx={{ display: 'flex', flexDirection: 'column', alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', maxWidth: '90%', flexDirection: msg.role === 'user' ? 'row-reverse' : 'row' }}>
                    {msg.role === 'assistant' && (
                      <Avatar sx={{ bgcolor: '#6366f1', width: 28, height: 28, flexShrink: 0 }}>
                        <SmartToyIcon sx={{ fontSize: 16, color: 'white' }} />
                      </Avatar>
                    )}
                    {msg.role === 'user' && (
                      <Avatar sx={{ bgcolor: '#a855f7', width: 28, height: 28, flexShrink: 0 }}>
                        {localStorage.getItem('era_user') &&
                          JSON.parse(localStorage.getItem('era_user')).name?.[0]?.toUpperCase()}
                      </Avatar>
                    )}
                    <Paper elevation={1} sx={{
                      p: 1.5,
                      bgcolor: msg.role === 'user' ? '#6366f1' : 'white',
                      color: msg.role === 'user' ? 'white' : 'text.primary',
                      borderRadius: 2,
                      ...(msg.role === 'user' ? { borderBottomRightRadius: 4 } : { borderBottomLeftRadius: 4, border: '1px solid rgba(0,0,0,0.05)' })
                    }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.82rem' }}
                        dangerouslySetInnerHTML={renderMessageContent(msg.content)} />
                    </Paper>
                  </Box>

                  {/* Rendu riche des résultats d'actions */}
                  {msg.role === 'assistant' && msg.action && msg.action !== 'NONE' && msg.action_result && (
                    <Box sx={{ ml: 4.5, mt: 1, maxWidth: '90%', width: '100%' }}>
                      <Paper elevation={1} sx={{ p: 1.5, border: '1px solid #e8eaf6', borderRadius: 2, bgcolor: 'white' }}>
                        <ActionResultRenderer action={msg.action} result={msg.action_result} />
                      </Paper>
                    </Box>
                  )}
                </Box>
              ))}

              {loading && (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: '#6366f1', width: 28, height: 28 }}>
                    <SmartToyIcon sx={{ fontSize: 16, color: 'white' }} />
                  </Avatar>
                  <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, borderBottomLeftRadius: 4, bgcolor: 'white', border: '1px solid rgba(0,0,0,0.05)', display: 'flex', gap: 0.5 }}>
                    {[0, 1, 2].map(i => (
                      <Box key={i} sx={{
                        width: 6, height: 6, borderRadius: '50%', bgcolor: '#6366f1',
                        animation: 'bounce 1s infinite', animationDelay: `${i * 0.2}s`,
                        '@keyframes bounce': {
                          '0%, 100%': { transform: 'translateY(0)', opacity: 0.5 },
                          '50%': { transform: 'translateY(-4px)', opacity: 1 }
                        }
                      }} />
                    ))}
                  </Paper>
                </Box>
              )}
              <div ref={messagesEndRef} />
            </Box>

            <Divider />

            {/* Input */}
            <Box sx={{ p: 1.5, bgcolor: 'background.paper' }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField fullWidth multiline maxRows={3}
                  placeholder={t('assistant.placeholder')}
                  value={input} onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress} disabled={loading} size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3, fontSize: '0.82rem' } }} />
                <IconButton color="primary" onClick={handleSend} disabled={!input.trim() || loading}
                  sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' }, '&.Mui-disabled': { bgcolor: 'action.disabledBackground' } }}>
                  <SendIcon />
                </IconButton>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', fontSize: '0.65rem' }}>
                💡 {t('assistant.hint')}
              </Typography>
            </Box>
          </>
        )}

        {/* Tab Planning */}
        {tabValue === 1 && (
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            <PlanningTab projectId={projectId} />
          </Box>
        )}

        {/* Tab Priorités */}
        {tabValue === 2 && (
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            <PrioritiesTab />
          </Box>
        )}
      </Box>
    </Drawer>
  );
}

// ─── FAB (Floating Action Button) ───────────────────────────────────────────

export function ERAAssistantFAB() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Détecter automatiquement le projectId depuis l'URL
  const projectMatch = location.pathname.match(/\/projects\/(\d+)/);
  const projectId = projectMatch ? parseInt(projectMatch[1]) : null;

  return (
    <>
      <Tooltip title={projectId ? t('assistant.fabProjectActive') : "ERA Assistant"} placement="left">
        <Fab color="primary" sx={{
          position: 'fixed', bottom: 24, right: 24,
          background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
          boxShadow: '0 4px 14px 0 rgba(99, 102, 241, 0.39)',
          '&:hover': {
            background: 'linear-gradient(135deg, #4f46e5 0%, #9333ea 100%)',
            boxShadow: '0 6px 20px 0 rgba(99, 102, 241, 0.23)'
          }
        }} onClick={() => setOpen(true)}>
          <Badge overlap="circular" anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            variant={projectId ? 'dot' : 'standard'}
            sx={{ '& .MuiBadge-badge': { bgcolor: '#44b700', color: '#44b700', boxShadow: '0 0 0 2px white' } }}>
            <SmartToyIcon />
          </Badge>
        </Fab>
      </Tooltip>
      <ERAAssistant projectId={projectId} open={open} onClose={() => setOpen(false)} />
    </>
  );
}

export default ERAAssistant;
